import { Text, TextArea as RadixTextArea } from '@radix-ui/themes'
import React, { useCallback, useEffect, useRef } from 'react'

type Props = React.ComponentProps<typeof RadixTextArea> & {
  grow?: boolean
  containerStyle?: React.CSSProperties
  textAreaStyle?: React.CSSProperties
  readOnly?: boolean
}

/**
 * Enhanced text area component with optional auto-growing height functionality.
 *
 * Extends Radix UI TextArea with dynamic height adjustment based on content.
 * When grow is enabled, the textarea automatically expands to fit its content.
 *
 * @param onChange - Callback function when the textarea value changes
 * @param grow - Whether to automatically adjust height based on content (default: false)
 * @param rest - All other props passed to the underlying Radix TextArea component
 * @see https://www.radix-ui.com/themes/docs/components/text-area - Radix UI TextArea documentation
 */
const TextArea = ({
  onChange,
  containerStyle,
  textAreaStyle,
  readOnly = false,
  grow = false,
  ...rest
}: Props): React.ReactNode => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const updateHeight = useCallback(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto'
      const height = `${textAreaRef.current.scrollHeight}px`
      textAreaRef.current.style.height = height
    }
  }, [])
  const handleChange = useCallback(
    e => {
      if (onChange) onChange(e)
      if (grow) updateHeight()
    },
    [grow, onChange, updateHeight],
  )
  useEffect(() => {
    if (grow) updateHeight()
  }, [grow, updateHeight, rest.value])
  return (
    <>
      {containerStyle && (
        <Text as="div" style={containerStyle}>
          {rest.value}
        </Text>
      )}
      <RadixTextArea
        {...rest}
        ref={textAreaRef}
        onChange={handleChange}
        readOnly={readOnly || rest.disabled}
        style={{
          ...(textAreaStyle || {}),
          ...(rest.style || {}),
        }}
      />
    </>
  )
}

export default TextArea
